import { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth, LoginPage } from 'auth-sso-kit/client/index.js';
import { DevEnvBanner } from 'env-kit/client/index.js';
import { RoleProvider, useRole } from './context/RoleContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/layout/AppShell';
import { TableauPage } from './pages/TableauPage';
import { NouvelleDemandePage } from './pages/NouvelleDemandePage';
import { DemandePage } from './pages/DemandePage';
import { UrgencePage } from './pages/UrgencePage';
import { RemplacantsPage } from './pages/RemplacantsPage';
import { AteliersPage } from './pages/AteliersPage';
import { FakPage } from './pages/FakPage';
import { AdminPage } from './pages/AdminPage';
import { MessagesAEnvoyerPage } from './pages/MessagesAEnvoyerPage';
import { ChatBubble } from './components/chatbot/ChatBubble';

// Routage par hash, maison (pas de lib). '#demandes/12' → { page:'demandes', param:12 }.
// '#nouvelle?absent=15&date=2026-07-20' → params pour pré-remplir un formulaire
// depuis un lien profond (ex. clic sur un trou de couverture du Calendrier,
// vue Calendrier du Tableau de bord — page autonome supprimée, Axe 2).
export type Page =
  | 'tableau' | 'nouvelle' | 'demandes' | 'urgence'
  | 'remplacants' | 'ateliers' | 'fak' | 'messages' | 'admin';

export interface RouteState { page: Page; param: number | null; params: Record<string, string>; }

const PAGES: Page[] = ['tableau', 'nouvelle', 'demandes', 'urgence', 'remplacants', 'ateliers', 'fak', 'messages', 'admin'];
const ADMIN_ONLY: Page[] = ['admin'];
const GESTION_ONLY: Page[] = ['messages'];
const CLE_RETOUR = 'retour_apres_connexion';

function parseHash(): RouteState {
  const [pathPart, queryPart] = (window.location.hash.slice(1) || 'tableau').split('?');
  const [raw, rawParam] = pathPart.split('/');
  const page = (PAGES as string[]).includes(raw) ? (raw as Page) : 'tableau';
  const param = rawParam ? Number(rawParam) || null : null;
  const params: Record<string, string> = {};
  if (queryPart) {
    for (const pair of queryPart.split('&')) {
      const [k, v] = pair.split('=');
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
    }
  }
  return { page, param, params };
}

export function naviguer(page: Page, param?: number, params?: Record<string, string>) {
  let hash = param != null ? `${page}/${param}` : page;
  const entries = Object.entries(params ?? {}).filter(([, v]) => v != null && v !== '');
  if (entries.length > 0) {
    hash += '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  }
  window.location.hash = hash;
}

function AppContent() {
  const { user, loading } = useAuth();
  const { isAdmin, peutGerer } = useRole();
  const [route, setRoute] = useState<RouteState>(parseHash);

  useEffect(() => {
    const onHash = () => {
      const r = parseHash();
      // Garde UX (le serveur applique les mêmes règles indépendamment). Seulement une fois la connexion terminée : avant,
      // les rôles ne sont pas encore connus et un lien direct (« #messages », « #admin ») serait renvoyé au tableau de bord.
      if (!loading && user) {
        if (ADMIN_ONLY.includes(r.page) && !isAdmin) { window.location.hash = 'tableau'; return; }
        if (GESTION_ONLY.includes(r.page) && !peutGerer) { window.location.hash = 'tableau'; return; }
      }
      setRoute(r);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [isAdmin, peutGerer, loading, user]);

  // Retour sur le lien d'origine après la connexion : un lien profond (par ex. celui du message Slack) ouvert
  // sans être connecté·e est mémorisé le temps de l'onglet, puis rouvert une fois connecté·e. La connexion
  // SSO repasse par le fournisseur d'identité et perd l'adresse (la connexion par mot de passe, non).
  // On ne rouvre le lien QUE si l'adresse a été perdue : un lien déjà dans la barre d'adresse est plus récent.
  // Rien n'est mémorisé après une déconnexion volontaire (le stockage ne suit pas le compte suivant).
  const dejaConnecte = useRef(false);
  useEffect(() => {
    if (loading) return;
    try {
      if (user) {
        dejaConnecte.current = true;
        const retour = sessionStorage.getItem(CLE_RETOUR);
        if (retour) {
          sessionStorage.removeItem(CLE_RETOUR);
          const actuel = window.location.hash;
          if (actuel === '' || actuel === '#' || actuel === '#tableau') window.location.hash = retour;
        }
        return;
      }
      if (dejaConnecte.current) return;
      const memoriser = () => {
        const h = window.location.hash;
        const page = h.slice(1).split(/[/?]/)[0];
        if (h !== '#tableau' && (PAGES as string[]).includes(page)) sessionStorage.setItem(CLE_RETOUR, h);
      };
      memoriser();
      // Un nouveau lien collé dans le même onglet, avant la connexion, remplace le précédent.
      window.addEventListener('hashchange', memoriser);
      return () => window.removeEventListener('hashchange', memoriser);
    } catch { /* stockage indisponible (navigation privée…) : comportement habituel */ }
  }, [user, loading]);

  if (loading) return <div className="login-page"><div className="spinner" /></div>;
  if (!user) {
    return (
      <LoginPage
        title="TacTac"
        subtitle="Gestion des remplacements d'ateliers"
        emailPlaceholder="prenom.nom@exemple.ch"
      />
    );
  }

  const rendu = () => {
    switch (route.page) {
      case 'nouvelle': return <NouvelleDemandePage prefill={route.params} />;
      case 'demandes': return route.param != null ? <DemandePage id={route.param} params={route.params} /> : <TableauPage params={route.params} />;
      case 'urgence': return <UrgencePage param={route.param} />;
      case 'remplacants': return <RemplacantsPage />;
      case 'ateliers': return <AteliersPage params={route.params} />;
      case 'fak': return <FakPage />;
      case 'messages': return <MessagesAEnvoyerPage />;
      case 'admin': return <AdminPage params={route.params} />;
      default: return <TableauPage params={route.params} />;
    }
  };

  return (
    <ThemeProvider>
      <AppShell page={route.page} onNavigate={naviguer}>
        {rendu()}
      </AppShell>
      <ChatBubble />
    </ThemeProvider>
  );
}

// Ordre des providers : Auth (kit) → Rôles (dérivés) → contenu.
// Bandeau dev (env-kit) à la racine, hors login, pour qu'il s'affiche partout.
export function App() {
  return (
    <>
      <AuthProvider>
        <RoleProvider>
          <AppContent />
        </RoleProvider>
      </AuthProvider>
      <DevEnvBanner />
    </>
  );
}
